import { AHText, Box } from "arkh";
import React from "react";
import {
  Platform,
  Alert as Native,
  AlertButton as ABP,
  Text,
} from "react-native";
import { useIntlTokens } from "../components/mobile/useIntlTokens";
import { OrgButton } from "../features/OrgHome/OrgButton";
import { WebPopup } from "./WebPopup";

const Alert = {
  confirm: async (
    title: string,
    message?: string,
    confirmMessage?: string
  ): Promise<boolean> => {
    return new Promise((resolve) => {
      Native.alert(title, message, [
        {
          text: "Cancel",
          onPress() {
            resolve(false);
          },
        },
        {
          text: confirmMessage ?? "OK",
          onPress() {
            resolve(true);
          },
        },
      ]);
    });
  },
};

function notifyError(label: string, description?: string) {
  if (Platform.OS === "web") {
    window.alert(label + " - " + (description ?? ""));
  } else {
    Native.alert(label, description ?? "");
  }
}

const AlertCtx = React.createContext({
  prompt: Native.prompt,
  alert: Native.alert,
  error: notifyError,
  confirm: Alert.confirm,
});
export const useAlert = () => React.useContext(AlertCtx);

type AlertState = {
  title: string;
  message?: string;
  buttons: ABP[];
};

function sortKey(button: ABP) {
  if (button.isPreferred) {
    return 9;
  }
  if (button.style === "cancel") {
    return 8;
  }
  if (button.style === "default") {
    return 7;
  }
  return 6;
}

function AlertText(props: ABP) {
  const intl_tokens = useIntlTokens();
  if (props.text) {
    return <Text>{props.text}</Text>;
  }
  switch (props.style) {
    case "cancel":
      return <Text>{intl_tokens.Cancel}</Text>;
    case "destructive":
      return <Text>{intl_tokens.Delete}</Text>;
    case "default":
      return <Text>{intl_tokens.OK}</Text>;
  }
  return null;
}
function AlertWebModal({
  state,
  onDismiss,
  onPressedButton,
}: {
  state: AlertState | null;
  onDismiss: () => void;
  onPressedButton: () => void;
}) {
  return (
    <WebPopup onDismiss={onDismiss}>
      {state ? (
        <>
          <AHText type="title">{state.title}</AHText>
          <AHText type="body" spaceVertical="medium">
            {state.message}
          </AHText>
          <Box style={{ flex: 1 }} />
          <AlertButtons
            buttons={state.buttons}
            onPressedButton={onPressedButton}
          />
        </>
      ) : null}
    </WebPopup>
  );
}

export function AlertButtons(props: {
  buttons: ABP[];
  onPressedButton?: () => void;
}) {
  return (
    <Box
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        flexWrap: "wrap",
      }}
    >
      {props.buttons
        .slice()
        .sort((a, b) => {
          return sortKey(a) - sortKey(b);
        })
        .map((button, index) => (
          <Box
            key={index + ""}
            spaceLeft={index === 0 ? "none" : "medium"}
            spaceBottom="medium"
          >
            <AlertButton
              onPress={() => {
                button.onPress?.();
                props.onPressedButton?.();
              }}
              title={<AlertText {...button} />}
              isPreferred={button.isPreferred ?? false}
              style={button.style}
            />
          </Box>
        ))}
    </Box>
  );
}

function AlertButton(props: {
  style: "default" | "cancel" | "destructive" | undefined;
  isPreferred: boolean;
  title: React.ReactNode;
  onPress: () => void;
}) {
  switch (props.style) {
    case "cancel":
      return (
        <OrgButton
          onPress={props.onPress}
          title={props.title}
          tintColor="text-primary"
          backgroundColor="background"
        />
      );
    case "destructive":
      return (
        <OrgButton
          onPress={props.onPress}
          title={props.title}
          backgroundColor="status-negative"
          tintColor="text-inverted"
        />
      );

    default:
      return props.isPreferred ? (
        <OrgButton
          onPress={props.onPress}
          title={props.title}
          tintColor="text-inverted"
          backgroundColor="primary"
        />
      ) : (
        <OrgButton
          onPress={props.onPress}
          title={props.title}
          backgroundColor="text-inverted"
          tintColor="primary"
        />
      );
  }
}
export function AlertWebProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<null | AlertState>(null);
  const intl = useIntlTokens();
  if (Platform.OS !== "web") {
    return <>{children}</>;
  }
  const alert = (title: string, message?: string, buttons?: ABP[]) => {
    setState({
      title,
      message,
      buttons: buttons ?? [
        {
          text: "OK",
          isPreferred: true,
          onPress: () => {},
        },
      ],
    });
  };

  return (
    <AlertCtx.Provider
      value={{
        error: notifyError,
        prompt: window.prompt,
        confirm: async (
          title: string,
          message?: string,
          confirmMessage?: string
        ): Promise<boolean> => {
          return new Promise((resolve) => {
            alert(title, message, [
              {
                text: intl.Cancel,
                onPress() {
                  resolve(false);
                },
              },
              {
                text: confirmMessage ?? "OK",
                onPress() {
                  resolve(true);
                },
              },
            ]);
          });
        },
        alert,
      }}
    >
      {state ? (
        <AlertWebModal
          state={state}
          onPressedButton={() => {
            setState(null);
          }}
          onDismiss={() => {
            setState(null);
            const button =
              state.buttons.find((b) => b.style === "cancel") ??
              state.buttons.find(
                (b) => b.style !== "destructive" && b.isPreferred
              );
            button?.onPress?.();
          }}
        />
      ) : null}
      {children}
    </AlertCtx.Provider>
  );
}
